"use client";

import { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { useAuthModal } from "@/features/auth/hooks/useAuthModal";
import { useUser } from "@/features/auth/hooks/useUser";
import {
  FREE_PLAN,
  PLAN_CONFIG,
  PLAN_HIGHLIGHTS,
  formatUsd,
  intervalSuffix,
  monthlyEquivalentCents,
  yearlySavingsPercent,
  type BillingInterval,
  type PaidPlan,
} from "@/lib/billing/planConfig";
import { useBilling } from "../hooks/useBilling";
import { hasLiveSubscription, planCardAction, type CardAction } from "../lib/planCta";
import { formatShortBillingDate } from "../lib/format";
import { ComingSoonButton } from "./ComingSoonButton";
import { PLAN_ACCENT } from "./planStyle";

const CARDS = ["free", "pro", "studio"] as const;
type Card = (typeof CARDS)[number];

type Props = {
  interval: BillingInterval;
  /** Analytics: which surface the cards are on. */
  source: string;
  /** Compact = the pricing modal; page = /pricing. */
  variant?: "page" | "compact";
  /** Ring a card (e.g. Studio when the modal opened from an Audience gate). */
  highlight?: PaidPlan | null;
};

function statusNote(card: Card, ctx: ReturnType<typeof useBilling>, plan: string): string | null {
  const { viewer, state } = ctx;
  if (card === "free" || card !== plan || viewer.complimentary) return null;
  if (viewer.status === "past_due") return "Last payment failed";
  if (viewer.status === "scheduled_cancel") return `Ends ${formatShortBillingDate(viewer.endsAt)}`;
  if (viewer.pending) {
    return `Switches to ${PLAN_CONFIG[viewer.pending.plan].label} on ${formatShortBillingDate(viewer.pending.effectiveAt)}`;
  }
  const next = state?.subscription?.nextChargeAt;
  if (next) return `Renews ${formatShortBillingDate(next)}`;
  return null;
}

export function PlanCards({ interval, source, variant = "page", highlight = null }: Props) {
  const billing = useBilling();
  const { viewer, load, pendingCheckout } = billing;
  const { plan, loading: userLoading } = useUser();
  const { open: openAuth } = useAuthModal();
  const [busy, setBusy] = useState<Card | null>(null);
  const [errors, setErrors] = useState<Partial<Record<Card, string>>>({});

  // Subscribers' cards depend on what Creem bills (interval, a pending
  // switch); everyone else's don't, and shouldn't cost a Creem call.
  const live = hasLiveSubscription(viewer);
  useEffect(() => {
    if (live) void load();
  }, [live, load]);

  async function run(card: Card, action: CardAction) {
    // Never reached (a coming-soon card renders no handler); refuse anyway.
    if (action.kind === "coming_soon") return;
    setErrors((e) => ({ ...e, [card]: undefined }));
    if (action.kind === "signup") {
      openAuth();
      return;
    }
    if (action.kind === "disabled") return;
    setBusy(card);
    const result =
      action.kind === "checkout" || action.kind === "change"
        ? await billing.choosePlan(action.plan, action.interval, source)
        : action.kind === "cancel"
          ? (billing.openCancel(source), { ok: true as const })
          : action.kind === "resume"
            ? await billing.resume()
            : await billing.openPortal();
    setBusy(null);
    if (!result.ok) setErrors((e) => ({ ...e, [card]: result.message }));
  }

  const compact = variant === "compact";

  return (
    <div className={`grid gap-3 ${compact ? "md:grid-cols-3" : "gap-4 lg:grid-cols-3"}`}>
      {CARDS.map((card) => {
        const paid = card !== "free";
        const accent = PLAN_ACCENT[card];
        // The prerendered HTML (and a crawler, or Creem's reviewer) sees the
        // signed-out buttons; only a session still being read shows "…".
        const action =
          userLoading && plan !== "anonymous"
            ? ({ kind: "disabled", label: "…", tone: "muted" } as const)
            : planCardAction(viewer, card, interval);
        const isCurrent = action.kind === "disabled" && action.tone === "current";
        const ringed = highlight === card || (!highlight && card === "pro" && !isCurrent && plan !== "pro" && plan !== "studio");
        const highlights = PLAN_HIGHLIGHTS[card];
        const price = paid ? PLAN_CONFIG[card].prices[interval] : null;
        const note = statusNote(card, billing, plan);
        const isBusy = busy === card || (paid && pendingCheckout === card);

        return (
          <section
            key={card}
            // /pricing#studio lands here (/terms, the API docs and the API's
            // plan_required error link to it); the modal and /account sit on
            // other pages, so only /pricing carries the anchor.
            id={card === "studio" && !compact ? "studio" : undefined}
            aria-labelledby={`plan-${card}-${variant}`}
            className={`relative flex scroll-mt-24 flex-col rounded-xl border bg-[#131c28] ${compact ? "p-4" : "p-6"} ${
              ringed ? "border-transparent ring-1" : "border-[#2a475e]"
            }`}
            style={ringed ? ({ "--tw-ring-color": accent } as React.CSSProperties) : undefined}
          >
            {(ringed && card === "pro" && !highlight) && (
              <span
                className="absolute -top-2.5 left-5 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#0d151e]"
                style={{ backgroundColor: accent }}
              >
                Recommended
              </span>
            )}
            <div className="flex items-baseline justify-between gap-2">
              <h3
                id={`plan-${card}-${variant}`}
                className={`${compact ? "text-base" : "text-lg"} font-semibold`}
                style={{ color: paid ? accent : "#e8eef3" }}
              >
                {paid ? PLAN_CONFIG[card].label : FREE_PLAN.label}
              </h3>
              {isCurrent && (
                <span className="rounded-full border border-[#a4d007]/30 bg-[#a4d007]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#a4d007]">
                  Your plan
                </span>
              )}
            </div>
            <p className="mt-1 text-[13px] leading-snug text-[#8f98a0]">
              {paid ? PLAN_CONFIG[card].tagline : FREE_PLAN.tagline}
            </p>

            <div className={compact ? "mt-3" : "mt-5"}>
              <div className="flex items-baseline gap-1">
                <span
                  className={`font-[family-name:var(--font-geist-mono)] font-semibold tabular-nums text-white ${
                    compact ? "text-2xl" : "text-4xl"
                  }`}
                >
                  {price ? price.display : "$0"}
                </span>
                <span className="text-sm text-[#8f98a0]">{paid ? intervalSuffix(interval) : "forever"}</span>
              </div>
              <p className="mt-1 h-4 text-xs text-[#8f98a0]">
                {paid && interval === "year"
                  ? `${formatUsd(monthlyEquivalentCents(card))}/mo billed yearly · save ${yearlySavingsPercent(card)}%`
                  : paid
                    ? // The prerendered page is monthly: the yearly price must be
                      // readable without touching the toggle.
                      `billed monthly · or ${PLAN_CONFIG[card].prices.year.display}/yr`
                    : "no card needed"}
              </p>
            </div>

            <div className={compact ? "mt-3" : "mt-5"}>
              {action.kind === "coming_soon" ? (
                // Not on sale yet: no handler to re-enable (see ComingSoonButton).
                <ComingSoonButton
                  className="flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-[#0d151e]"
                  style={{ backgroundColor: accent }}
                >
                  {action.label}
                </ComingSoonButton>
              ) : (
                <button
                  type="button"
                  disabled={action.kind === "disabled" || isBusy}
                  onClick={() => run(card, action)}
                  className={`flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-[filter,border-color] disabled:cursor-default ${
                    action.kind === "disabled"
                      ? action.tone === "current"
                        ? "border border-[#2a475e] bg-transparent text-[#c7d5e0]"
                        : "border border-[#2a475e]/60 bg-transparent text-[#8f98a0]"
                      : action.kind === "cancel"
                        ? "border border-[#2a475e] bg-[#16202d] text-[#c7d5e0] hover:border-[#3a556e] hover:text-white"
                        : action.kind === "fix_payment"
                          ? "bg-[#e35e1c] text-white hover:brightness-110"
                          : paid
                            ? "text-[#0d151e] hover:brightness-110"
                            : "border border-[#2a475e] bg-[#16202d] text-white hover:border-[#3a556e]"
                  }`}
                  style={
                    action.kind !== "disabled" && action.kind !== "cancel" && action.kind !== "fix_payment" && paid
                      ? { backgroundColor: accent }
                      : undefined
                  }
                >
                  {isBusy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
                  {isBusy && action.kind === "checkout" ? "Opening checkout…" : action.label}
                </button>
              )}
              <p className={`mt-1.5 min-h-4 text-center text-[11px] ${errors[card] ? "text-red-400" : "text-[#8f98a0]"}`}>
                {errors[card] ?? note ?? ""}
              </p>
            </div>

            <div className={`${compact ? "mt-2" : "mt-4"} border-t border-[#2a475e]/60 pt-4`}>
              {highlights.lead && (
                <p className="mb-2 text-xs font-medium text-[#8f98a0]">{highlights.lead}:</p>
              )}
              <ul className="space-y-2">
                {highlights.items.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-[13px] leading-snug text-[#c7d5e0]">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: paid ? accent : "#a4d007" }} aria-hidden />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        );
      })}
    </div>
  );
}
